/**
 * React Query Hook: Deduct Credits
 *
 * Hook for deducting credits from the current user's balance.
 * Endpoint: POST /api/credits/deduct
 */

import {
  useMutation,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { deductCredits } from "@/api/endpoints/credits";
import type {
  DeductCreditsRequest,
  DeductCreditsResponse,
} from "@/api/types/credits";
import { queryKeys } from "@/lib/queryClient";

/**
 * Hook to deduct credits from the current user's balance
 *
 * Features:
 * - Automatically invalidates credit balance + history + check-action queries on success
 * - Provides loading state during mutation
 *
 * @returns React Query mutation result
 */
export function useDeductCredits(): UseMutationResult<
  DeductCreditsResponse,
  Error,
  DeductCreditsRequest,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deductCredits,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.credits.all });
    },
  });
}
